/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useState } from 'react';
import {
    HighContrastSelector,
    TooltipHost,
    IContextualMenuItem,
    IIconProps,
    DefaultButton,
    CommandBar,
    ICommandBarItemProps,
} from '@fluentui/react';
import { IconButton, IButtonStyles } from '@fluentui/react/lib/Button';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    CurrentDeviceListItemDto,
    DeviceStatus,
} from '../../../Services/POS/PosService';
import { Actions } from '../../../common/config/constants';
import './CommandBarActions.scss';
import { getBooleanValue } from '../../../helpers/utils';
import { POSDeviceStatusAction } from '../TaxpayerProfile';
import { ReduxState } from '../../../common/reducers';

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export interface Props {
    type: string;
    items: IContextualMenuItem[];
    onActionClick: (type: string) => void;
    canChangeStatus?: boolean;
    canChangeRetire?: boolean;
    RetireStatus?: POSDeviceStatusAction;
    deactivateStatus?: POSDeviceStatusAction;
    selectedDevices: CurrentDeviceListItemDto[];
    devicesFiltered: boolean;
}

const addIcon: IIconProps = { iconName: 'Add' };

const CommandBarActions: FC<Props & PropsFromRedux> = (
    props: Props & PropsFromRedux
) => {
    const {
        type,
        items,
        onActionClick,
        canChangeStatus,
        deactivateStatus,
        canChangeRetire,
        RetireStatus,
        selectedDevices,
        devicesFiltered,
        currentLanguage,
    } = props;
    const { t } = useTranslation(['resources', 'pos']);
    const [currentName, setCurrentName] = useState<string>();
    const [showCommands, setShowCommands] = useState<boolean>(false);
    const PredefinedMobileModel = (window as any)
        .REACT_APP_PredefinedMobileModel;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const registerPOSConfig = getBooleanValue(
        (window as any).REACT_APP_RegisterPOS
    );
    const isDeviceDeactivatedByAdmin = selectedDevices.some(
        (x) => x.isStatusUpdatedByAdmin === true
    );

    const isDeviceRetireOrRetiring = selectedDevices.some(
        (x) =>
            x.status === DeviceStatus.Retired ||
            x.status === DeviceStatus.Retiring
    );
    const isMobilePOS = selectedDevices.some(
        (x) => x?.modelName === PredefinedMobileModel
    );

    const GetDeactivateToolTipContent = (): string => {
        if (
            isDeviceDeactivatedByAdmin &&
            deactivateStatus === POSDeviceStatusAction.Deactive
        )
            return selectedDevices.length > 1
                ? t('pos:DeactivateMultiplePOSTooltip')
                : t('pos:DeactivatePOSTooltip');
        return canChangeStatus ? '' : t('pos:noChangeForMixed');
    };

    const devicesCommandBarItems: ICommandBarItemProps[] = [
        {
            key: 'Retire',
            className: 'commandAction',
            text: t(
                RetireStatus === POSDeviceStatusAction.Active
                    ? 'pos:Retire'
                    : 'pos:revert'
            ),
            disabled: !canChangeRetire || isMobilePOS,
            iconProps: {
                iconName:
                    RetireStatus === POSDeviceStatusAction.Active
                        ? 'RecycleBin'
                        : 'EmptyRecycleBin',
            },
            onClick: () => {
                onActionClick(Actions.Retire);
            },
        },
        {
            key: 'ClearFilters',
            className: 'commandAction',
            text: t('pos:clearFilters'),
            disabled: !devicesFiltered || isDeviceRetireOrRetiring,
            iconProps: { iconName: 'ClearFilter' },
            onClick: () => {
                onActionClick(Actions.ClearFilters);
            },
        },
        {
            key: 'Deactivate',
            className: 'commandAction',
            disabled: !canChangeStatus || isDeviceRetireOrRetiring,
            onRender: () => (
                <TooltipHost
                    content={GetDeactivateToolTipContent()}
                    calloutProps={{ calloutMaxWidth: 200 }}
                    className="POSDevice_DeactivateButton_Tooltip"
                >
                    <DefaultButton
                        className="commandAction deactivateButton"
                        disabled={!canChangeStatus || isDeviceRetireOrRetiring}
                        onClick={() => {
                            onActionClick(Actions.Deactivate);
                        }}
                        iconProps={{
                            iconName:
                                deactivateStatus ===
                                POSDeviceStatusAction.Active
                                    ? 'StatusCircleBlock'
                                    : 'PlayResume',
                        }}
                    >
                        {t(
                            deactivateStatus === POSDeviceStatusAction.Active
                                ? 'pos:deactivate'
                                : 'pos:reactivate'
                        )}
                    </DefaultButton>
                </TooltipHost>
            ),
        },
        {
            key: 'Export',
            className: 'commandAction',
            text: t('pos:export'),
            iconProps: { iconName: 'DownloadDocument' },
            onClick: () => {
                onActionClick(Actions.Export);
            },
        },
    ];

    useEffect(() => {
        let name;
        setShowCommands(false);
        switch (type) {
            case 'pos':
                name = registerPOSConfig ? t('pos:RegisterPOS2') : undefined;
                setShowCommands(true);
                break;
            case 'user':
                name = t('TaxpayerUserGrid.InviteUser');
                break;
            case 'erp':
                name = t('TaxpayerUserGrid.RegisterErp');
                break;
            case 'intermediary':
                name = t('form.Taxpayer.AddIntermediary');
                break;
            default:
                break;
        }
        setCurrentName(name);
    }, [type, currentLanguage]);

    const customSplitButtonStyles: IButtonStyles = {
        splitButtonMenuButton: {
            backgroundColor: 'white',
            width: 28,
            border: 'none',
        },
        splitButtonMenuIcon: { fontSize: '7px' },
        splitButtonDivider: {
            backgroundColor: '#c8c8c8',
            width: 1,
            right: 26,
            position: 'absolute',
            top: 4,
            bottom: 4,
        },
        splitButtonContainer: {
            selectors: {
                [HighContrastSelector]: { border: 'none' },
            },
        },
    };

    return (
        <div className="commandBarActions">
            {items.length > 0 && (
                <>
                    {showCommands && (
                        <CommandBar
                            items={[]}
                            farItems={devicesCommandBarItems}
                        />
                    )}
                    <div className="commandContainer">
                        {currentName && (
                            <DefaultButton
                                split
                                className="command"
                                text={currentName}
                                iconProps={addIcon}
                                onClick={() => onActionClick('none')}
                            />
                        )}
                        <div className="vertical-line">
                            <div className="line">&#124;</div>
                        </div>
                        <IconButton
                            className="buttonMenu"
                            // iconProps={addIcon}
                            // styles={customSplitButtonStyles}
                            menuProps={{ items }}
                            onClick={() => onActionClick('none')}
                        />
                    </div>
                </>
            )}
        </div>
        // <CommandButton iconProps={addIcon} text={currentName} menuProps={menuProps} />
    );
};
export default connector(CommandBarActions);
